// src/pages/ShopPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase";

function normalizeText(str = "") {
  return String(str)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function formatMoney(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return "Prix non défini";
  return `${n.toLocaleString("fr-FR")} FCFA`;
}

function slugify(str = "") {
  return String(str)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function getProductName(product) {
  return (
    product?.shortName ||
    product?.name ||
    product?.Name ||
    product?.title ||
    product?.Title ||
    product?.productName ||
    product?.ProductName ||
    "Produit sans nom"
  );
}

function getProductImage(product) {
  return (
    product?.ImageUrl ||
    product?.imageUrl ||
    product?.image ||
    product?.mainImage ||
    (Array.isArray(product?.images) && product.images[0]) ||
    ""
  );
}

function getProductPrice(product) {
  return (
    product?.priceXof ??
    product?.salePrice ??
    product?.SalePrice ??
    product?.price ??
    product?.Price ??
    product?.publicPrice ??
    product?.Cass_Stock_Price ??
    null
  );
}

function getProductLink(product) {
  const id = product?.id;
  if (!id) return "#";

  const slug = product?.slug || product?.Slug || slugify(getProductName(product));
  return `/ip/${slug}/${id}`;
}

function parseCategories(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean);

  return String(raw)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export default function ShopPage() {
  const { vendorId } = useParams();

  const [loading, setLoading] = useState(true);
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("recent");

  const shopName =
    shop?.shopName || shop?.displayName || shop?.name || "Boutique GoShop";

  const shopDescription =
    shop?.shopDescription ||
    shop?.description ||
    "Découvrez les produits disponibles dans cette boutique sur GoShop.";

  const shareShopUrl = `https://goshopforall.com/l/shop-${vendorId}`;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [vendorId]);

  useEffect(() => {
    let cancelled = false;

    async function loadShop() {
      setLoading(true);
      setError("");

      try {
        if (!vendorId) {
          setError("Boutique introuvable.");
          return;
        }

        const vendorSnap = await getDoc(doc(db, "users", vendorId));

        if (!vendorSnap.exists()) {
          setError("Cette boutique est introuvable.");
          setShop(null);
          setProducts([]);
          return;
        }

        const vendorData = {
          id: vendorSnap.id,
          ...vendorSnap.data(),
        };

        let productsSnap;

        try {
          productsSnap = await getDocs(
            query(
              collection(db, "productsShop"),
              where("vendorId", "==", vendorId),
              where("validationStatus", "==", "validated"),
              orderBy("__name__", "desc")
            )
          );
        } catch {
          productsSnap = await getDocs(
            query(
              collection(db, "productsShop"),
              where("vendorId", "==", vendorId),
              where("validationStatus", "==", "validated")
            )
          );
        }

        const items = productsSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        if (!cancelled) {
          setShop(vendorData);
          setProducts(items);
        }
      } catch (err) {
        console.error("Erreur chargement boutique :", err);
        if (!cancelled) {
          setError("Impossible de charger cette boutique.");
          setShop(null);
          setProducts([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadShop();

    return () => {
      cancelled = true;
    };
  }, [vendorId]);

  const shopCategories = useMemo(() => parseCategories(shop?.categories), [shop]);

  const filteredProducts = useMemo(() => {
    const needle = normalizeText(search);
    let list = [...products];

    if (needle) {
      list = list.filter((p) => {
        const haystack = normalizeText(
          [
            getProductName(p),
            p?.description,
            p?.categoryLabel,
            p?.subCategoryLabel,
            p?.barcode,
          ]
            .filter(Boolean)
            .join(" ")
        );

        return haystack.includes(needle);
      });
    }

    if (sortBy === "price_asc") {
      list.sort(
        (a, b) => Number(getProductPrice(a) || 0) - Number(getProductPrice(b) || 0)
      );
    } else if (sortBy === "price_desc") {
      list.sort(
        (a, b) => Number(getProductPrice(b) || 0) - Number(getProductPrice(a) || 0)
      );
    } else if (sortBy === "name_asc") {
      list.sort((a, b) => getProductName(a).localeCompare(getProductName(b), "fr"));
    } else {
      list.sort((a, b) => {
        const aDate =
          a?.createdAt?.toMillis?.() ||
          a?.CreateAt?.toMillis?.() ||
          a?.CreatedAt?.toMillis?.() ||
          0;

        const bDate =
          b?.createdAt?.toMillis?.() ||
          b?.CreateAt?.toMillis?.() ||
          b?.CreatedAt?.toMillis?.() ||
          0;

        return bDate - aDate;
      });
    }

    return list;
  }, [products, search, sortBy]);

  const whatsappHref = useMemo(() => {
    const rawPhone = String(shop?.phone || "").trim();
    if (!rawPhone) return "";

    const cleaned = rawPhone.replace(/[^\d+]/g, "");
    const text = encodeURIComponent(
      `Bonjour ${shopName}, je vous contacte depuis votre boutique sur GoShop.`
    );

    return `https://wa.me/${cleaned.replace(/\+/g, "")}?text=${text}`;
  }, [shop, shopName]);

  const openAppShop = () => {
    window.location.href = `goshop://shop/${vendorId}`;

    setTimeout(() => {
      alert("Si GoShop ne s’ouvre pas, installez ou ouvrez l’application GoShop.");
    }, 1200);
  };

  const shareShopLink = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: shopName,
          text: `Découvrez la boutique ${shopName} sur GoShop`,
          url: shareShopUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareShopUrl);
        alert("Lien de la boutique copié ✅");
      }
    } catch {}
  };

  const shareProductLink = async ({ productId, title }) => {
    const url = `https://goshopforall.com/l/product-${productId}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title,
          text: `Découvrez ce produit sur GoShop`,
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        alert("Lien produit copié ✅");
      }
    } catch {}
  };

  if (loading) {
    return (
      <div className="shop-page">
        <div className="shop-loader">
          <div className="spinner" />
          <p>Chargement de la boutique…</p>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  if (error || !shop) {
    return (
      <div className="shop-page">
        <div className="shop-page-inner">
          <div className="shop-error-card">
            <div className="error-icon">🏪</div>
            <h1>Boutique indisponible</h1>
            <p>{error || "Cette boutique est introuvable."}</p>

            <Link to="/catalog" className="shop-btn shop-btn-primary">
              Retour au catalogue
            </Link>
          </div>
        </div>

        <style>{styles}</style>
      </div>
    );
  }

  return (
    <div className="shop-page">
      <div className="shop-page-inner">
        <section className="shop-hero">
          <div className="shop-banner-wrap">
            {shop?.bannerUrl ? (
              <img src={shop.bannerUrl} alt={shopName} className="shop-banner" />
            ) : (
              <div className="shop-banner-fallback" />
            )}

            <div className="shop-overlay" />

            <div className="shop-logo-wrap">
              {shop?.logoUrl ? (
                <img src={shop.logoUrl} alt={shopName} className="shop-logo" />
              ) : (
                <div className="shop-logo-fallback">
                  {String(shopName).charAt(0).toUpperCase()}
                </div>
              )}
            </div>
          </div>

          <div className="shop-hero-body">
            <div className="shop-breadcrumb">
              <Link to="/">Accueil</Link>
              <span>›</span>
              <Link to="/catalog">Catalogue</Link>
              <span>›</span>
              <span>{shopName}</span>
            </div>

            <div className="shop-header-row">
              <div className="shop-main-info">
                <span className="shop-badge">Boutique GoShop</span>
                <h1>{shopName}</h1>
                <p>{shopDescription}</p>
              </div>

              <div className="shop-actions">
                <button onClick={openAppShop} className="shop-btn shop-btn-app">
                  Ouvrir dans GoShop
                </button>

                <button onClick={shareShopLink} className="shop-btn shop-btn-light">
                  Partager la boutique
                </button>

                {whatsappHref ? (
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noreferrer"
                    className="shop-btn shop-btn-whatsapp"
                  >
                    WhatsApp
                  </a>
                ) : null}
              </div>
            </div>

            {shopCategories.length > 0 ? (
              <div className="shop-categories">
                {shopCategories.map((cat, idx) => (
                  <span key={`${cat}-${idx}`} className="shop-chip">
                    {cat}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        </section>

        <section className="shop-toolbar-card">
          <div className="shop-toolbar">
            <div>
              <h2>Produits de la boutique</h2>
              <p>
                {filteredProducts.length} produit
                {filteredProducts.length > 1 ? "s" : ""} affiché
                {filteredProducts.length > 1 ? "s" : ""}
              </p>
            </div>

            <div className="shop-toolbar-right">
              <input
                type="search"
                className="shop-search"
                placeholder="Rechercher un produit…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              <select
                className="shop-sort"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="recent">Plus récents</option>
                <option value="price_asc">Prix croissant</option>
                <option value="price_desc">Prix décroissant</option>
                <option value="name_asc">Nom A → Z</option>
              </select>
            </div>
          </div>
        </section>

        {filteredProducts.length === 0 ? (
          <div className="shop-empty-card">
            <h3>Aucun produit trouvé</h3>
            <p>
              {search
                ? "Aucun produit ne correspond à votre recherche dans cette boutique."
                : "Cette boutique n’a pas encore de produits validés."}
            </p>
          </div>
        ) : (
          <div className="shop-grid">
            {filteredProducts.map((product) => {
              const name = getProductName(product);
              const image = getProductImage(product);
              const price = getProductPrice(product);
              const link = getProductLink(product);
              const appProductLink = `goshop://product/${product.id}`;

              return (
                <article key={product.id} className="shop-product-card">
                  <Link to={link} className="shop-product-image-wrap">
                    {image ? (
                      <img
                        src={image}
                        alt={name}
                        className="shop-product-image"
                        loading="lazy"
                      />
                    ) : (
                      <div className="shop-product-image-fallback">
                        {String(name).charAt(0).toUpperCase()}
                      </div>
                    )}
                  </Link>

                  <div className="shop-product-body">
                    {(product?.categoryLabel || product?.subCategoryLabel) && (
                      <div className="shop-product-meta">
                        {product?.categoryLabel || "Catégorie"}
                        {product?.subCategoryLabel ? ` · ${product.subCategoryLabel}` : ""}
                      </div>
                    )}

                    <Link to={link} className="shop-product-name">
                      {name}
                    </Link>

                    <div className="shop-product-price">
                      {price != null ? formatMoney(price) : "Prix non défini"}
                    </div>

                    {product?.description ? (
                      <p className="shop-product-desc">
                        {String(product.description).length > 95
                          ? `${String(product.description).slice(0, 95)}…`
                          : String(product.description)}
                      </p>
                    ) : null}

                    <div className="shop-product-actions">
                      <a
                        href={appProductLink}
                        className="shop-btn shop-btn-primary shop-btn-sm"
                      >
                        Ouvrir
                      </a>

                      <button
                        type="button"
                        onClick={() =>
                          shareProductLink({
                            productId: product.id,
                            title: name,
                          })
                        }
                        className="shop-btn shop-btn-light shop-btn-sm"
                      >
                        Partager
                      </button>

                      <Link to={link} className="shop-btn shop-btn-light shop-btn-sm">
                        Détails
                      </Link>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      <style>{styles}</style>
    </div>
  );
}

const styles = `
  *{ box-sizing:border-box; }

  .shop-page{
    min-height:100vh;
    background:
      linear-gradient(180deg,#fff7ed 0%,#f8fafc 34%,#ffffff 100%);
    padding:18px 12px 44px;
    color:#0f172a;
    font-family:Inter,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;
  }

  .shop-page-inner{
    width:100%;
    max-width:1180px;
    margin:0 auto;
  }

  .shop-loader{
    min-height:80vh;
    display:flex;
    flex-direction:column;
    align-items:center;
    justify-content:center;
    gap:12px;
    color:#64748b;
    font-weight:800;
  }

  .spinner{
    width:42px;
    height:42px;
    border-radius:999px;
    border:4px solid #fed7aa;
    border-top-color:#f97316;
    animation:spin .8s linear infinite;
  }

  @keyframes spin{ to{ transform:rotate(360deg); } }

  .shop-error-card,
  .shop-empty-card{
    background:#fff;
    border:1px solid #eee;
    border-radius:28px;
    padding:30px;
    box-shadow:0 20px 55px rgba(15,23,42,.08);
  }

  .shop-error-card h1,
  .shop-empty-card h3{
    margin:0 0 8px;
    color:#0f172a;
    font-weight:950;
  }

  .shop-error-card p,
  .shop-empty-card p{
    color:#64748b;
    line-height:1.6;
  }

  .error-icon{
    width:60px;
    height:60px;
    border-radius:22px;
    background:#fff7ed;
    display:flex;
    align-items:center;
    justify-content:center;
    font-size:30px;
    margin-bottom:14px;
  }

  .shop-hero{
    position:relative;
    overflow:hidden;
    border-radius:34px;
    background:#fff;
    border:1px solid rgba(226,232,240,.9);
    box-shadow:0 24px 70px rgba(15,23,42,.10);
    margin-bottom:18px;
  }

  .shop-banner-wrap{
    position:relative;
    width:100%;
    height:260px;
    background:#0f172a;
  }

  .shop-banner{
    width:100%;
    height:100%;
    object-fit:cover;
    display:block;
  }

  .shop-banner-fallback{
    width:100%;
    height:100%;
    background:
      radial-gradient(circle at 20% 20%,rgba(255,255,255,.35),transparent 28%),
      linear-gradient(135deg,#f97316 0%,#16a34a 100%);
  }

  .shop-overlay{
    position:absolute;
    inset:0;
    background:linear-gradient(to top,rgba(15,23,42,.68),rgba(15,23,42,.05));
  }

  .shop-logo-wrap{
    position:absolute;
    left:28px;
    bottom:-48px;
    width:108px;
    height:108px;
    border-radius:30px;
    background:#fff;
    border:5px solid #fff;
    overflow:hidden;
    box-shadow:0 18px 45px rgba(15,23,42,.28);
    display:flex;
    align-items:center;
    justify-content:center;
  }

  .shop-logo{
    width:100%;
    height:100%;
    object-fit:cover;
  }

  .shop-logo-fallback{
    width:100%;
    height:100%;
    display:flex;
    align-items:center;
    justify-content:center;
    background:linear-gradient(135deg,#fff7ed,#ecfdf5);
    color:#f97316;
    font-size:38px;
    font-weight:950;
  }

  .shop-hero-body{
    padding:64px 28px 26px;
  }

  .shop-breadcrumb{
    display:flex;
    gap:8px;
    flex-wrap:wrap;
    font-size:12px;
    color:#94a3b8;
    font-weight:800;
    margin-bottom:12px;
  }

  .shop-breadcrumb a{
    color:#f97316;
    text-decoration:none;
  }

  .shop-header-row{
    display:flex;
    justify-content:space-between;
    gap:22px;
    flex-wrap:wrap;
    align-items:flex-start;
  }

  .shop-main-info{
    flex:1;
    min-width:260px;
  }

  .shop-badge{
    display:inline-flex;
    padding:7px 12px;
    border-radius:999px;
    background:#fff7ed;
    color:#c2410c;
    font-size:11px;
    font-weight:950;
    text-transform:uppercase;
    letter-spacing:.08em;
    margin-bottom:10px;
  }

  .shop-main-info h1{
    margin:0 0 8px;
    font-size:36px;
    line-height:1.05;
    font-weight:950;
    letter-spacing:-.04em;
  }

  .shop-main-info p{
    margin:0;
    max-width:720px;
    color:#64748b;
    font-size:15px;
    line-height:1.65;
  }

  .shop-actions{
    display:flex;
    gap:10px;
    flex-wrap:wrap;
    justify-content:flex-end;
  }

  .shop-categories{
    display:flex;
    gap:8px;
    flex-wrap:wrap;
    margin-top:18px;
  }

  .shop-chip{
    padding:7px 12px;
    border-radius:999px;
    background:#ecfdf5;
    color:#15803d;
    border:1px solid #bbf7d0;
    font-size:12px;
    font-weight:900;
  }

  .shop-toolbar-card{
    position:sticky;
    top:10px;
    z-index:5;
    background:rgba(255,255,255,.92);
    backdrop-filter:blur(14px);
    border:1px solid #e5e7eb;
    border-radius:26px;
    padding:16px;
    box-shadow:0 14px 34px rgba(15,23,42,.06);
    margin-bottom:18px;
  }

  .shop-toolbar{
    display:flex;
    justify-content:space-between;
    align-items:center;
    gap:16px;
    flex-wrap:wrap;
  }

  .shop-toolbar h2{
    margin:0 0 4px;
    font-size:21px;
    font-weight:950;
  }

  .shop-toolbar p{
    margin:0;
    color:#64748b;
    font-size:13px;
    font-weight:800;
  }

  .shop-toolbar-right{
    display:flex;
    gap:10px;
    flex-wrap:wrap;
  }

  .shop-search,
  .shop-sort{
    height:46px;
    border:1px solid #e2e8f0;
    border-radius:999px;
    background:#fff;
    padding:0 16px;
    font-size:13px;
    outline:none;
    font-weight:750;
  }

  .shop-search{ min-width:310px; }

  .shop-search:focus,
  .shop-sort:focus{
    border-color:#f97316;
    box-shadow:0 0 0 4px rgba(249,115,22,.12);
  }

  .shop-grid{
    display:grid;
    grid-template-columns:repeat(4,minmax(0,1fr));
    gap:16px;
  }

  .shop-product-card{
    background:#fff;
    border:1px solid #eef2f7;
    border-radius:26px;
    overflow:hidden;
    box-shadow:0 12px 30px rgba(15,23,42,.055);
    transition:.2s ease;
  }

  .shop-product-card:hover{
    transform:translateY(-4px);
    box-shadow:0 22px 50px rgba(15,23,42,.10);
  }

  .shop-product-image-wrap{
    display:block;
    aspect-ratio:1/1;
    background:#fff;
    border-bottom:1px solid #f1f5f9;
  }

  .shop-product-image{
    width:100%;
    height:100%;
    object-fit:contain;
    padding:16px;
    display:block;
  }

  .shop-product-image-fallback{
    width:100%;
    height:100%;
    display:flex;
    align-items:center;
    justify-content:center;
    font-size:34px;
    font-weight:950;
    color:#94a3b8;
    background:linear-gradient(135deg,#f8fafc,#eef2f7);
  }

  .shop-product-body{
    padding:14px;
    display:flex;
    flex-direction:column;
    gap:8px;
  }

  .shop-product-meta{
    font-size:11px;
    color:#94a3b8;
    font-weight:850;
  }

  .shop-product-name{
    min-height:40px;
    color:#0f172a;
    text-decoration:none;
    font-size:15px;
    line-height:1.35;
    font-weight:950;
  }

  .shop-product-name:hover{ color:#f97316; }

  .shop-product-price{
    font-size:17px;
    color:#16a34a;
    font-weight:950;
  }

  .shop-product-desc{
    margin:0;
    font-size:12px;
    color:#64748b;
    line-height:1.5;
    min-height:36px;
  }

  .shop-product-actions{
    display:grid;
    grid-template-columns:1fr 1fr;
    gap:8px;
    margin-top:4px;
  }

  .shop-product-actions .shop-btn-light:last-child{
    grid-column:1 / -1;
  }

  .shop-btn{
    display:inline-flex;
    align-items:center;
    justify-content:center;
    border-radius:999px;
    text-decoration:none;
    font-weight:950;
    transition:.18s ease;
    border:1px solid transparent;
    cursor:pointer;
    font-family:inherit;
    white-space:nowrap;
  }

  .shop-btn-sm{
    padding:9px 12px;
    font-size:12px;
  }

  .shop-btn-primary,
  .shop-btn-app{
    background:#f97316;
    color:#fff;
    border-color:#f97316;
    box-shadow:0 10px 22px rgba(249,115,22,.18);
  }

  .shop-btn-primary{
    padding:10px 16px;
    font-size:13px;
  }

  .shop-btn-app{
    padding:12px 17px;
    font-size:13px;
  }

  .shop-btn-primary:hover,
  .shop-btn-app:hover{
    background:#0f172a;
    border-color:#0f172a;
    color:#fff;
  }

  .shop-btn-light{
    background:#fff;
    color:#111827;
    border-color:#e5e7eb;
    padding:10px 16px;
    font-size:13px;
  }

  .shop-btn-light:hover{
    background:#f8fafc;
  }

  .shop-btn-whatsapp{
    background:#16a34a;
    color:#fff;
    border-color:#16a34a;
    padding:10px 16px;
    font-size:13px;
  }

  .shop-btn-whatsapp:hover{
    background:#15803d;
    border-color:#15803d;
  }

  @media (max-width:1100px){
    .shop-grid{ grid-template-columns:repeat(3,minmax(0,1fr)); }
  }

  @media (max-width:780px){
    .shop-page{ padding:12px 10px 32px; }
    .shop-banner-wrap{ height:200px; }
    .shop-logo-wrap{
      left:18px;
      width:90px;
      height:90px;
      bottom:-34px;
      border-radius:24px;
    }
    .shop-hero-body{ padding:50px 16px 18px; }
    .shop-main-info h1{ font-size:27px; }
    .shop-main-info p{ font-size:14px; }
    .shop-actions{ width:100%; }
    .shop-btn{ flex:1; }
    .shop-toolbar{ align-items:stretch; }
    .shop-toolbar-right{
      width:100%;
      flex-direction:column;
    }
    .shop-search,
    .shop-sort{
      width:100%;
      min-width:100%;
    }
    .shop-grid{
      grid-template-columns:repeat(2,minmax(0,1fr));
      gap:12px;
    }
    .shop-toolbar-card{
      position:relative;
      top:auto;
    }
  }

  @media (max-width:520px){
    .shop-product-body{ padding:11px; }
    .shop-product-name{
      font-size:13px;
      min-height:36px;
    }
    .shop-product-price{ font-size:14px; }
    .shop-product-desc{ display:none; }
    .shop-product-actions{
      grid-template-columns:1fr;
    }
    .shop-product-actions .shop-btn-light:last-child{
      grid-column:auto;
    }
  }
`;